<template>
  <aside
    :class="[
      'fixed inset-y-0 left-0 z-50 flex flex-col w-72 border-r border-white/40 dark:border-white/10 glass-panel transition-transform duration-300 ease-in-out',
      mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
    ]"
    aria-label="Main navigation"
  >
    <div class="relative flex flex-col items-center p-6 border-b border-white/30 dark:border-white/10 text-center">
      <router-link :to="{ path: '/', hash: '#home' }" class="group flex flex-col items-center gap-3" @click="$emit('close-mobile')">
        <ProfileAvatar :src="images.profile" alt="Profile photo" size="md" glow glass />
        <p class="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
          {{ siteState.profile.title }}
        </p>
      </router-link>
      <button
        type="button"
        class="lg:hidden absolute top-4 right-4 p-2 rounded-lg glass-subtle text-muted hover:text-foreground transition-colors"
        aria-label="Close menu"
        @click="$emit('close-mobile')"
      >
        <AppIcon name="X" :size="20" />
      </button>
    </div>

    <nav class="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-1">
      <router-link
        v-for="item in items"
        :key="item.section"
        :to="{ path: '/', hash: `#${item.section}` }"
        :class="navLinkClasses(item.section)"
        @click="$emit('close-mobile')"
      >
        <AppIcon :name="item.icon" :size="18" />
        <span>{{ item.name }}</span>
      </router-link>

      <template v-if="isAdmin">
        <p class="px-3 py-2 mt-6 text-xs font-semibold uppercase tracking-wider text-muted">Admin</p>
        <router-link
          v-for="item in adminItems"
          :key="item.path"
          :to="item.path"
          :class="adminLinkClasses(item.path)"
          @click="$emit('close-mobile')"
        >
          <AppIcon :name="item.icon" :size="18" />
          <span>{{ item.name }}</span>
        </router-link>
      </template>
    </nav>

    <div class="p-4 border-t border-white/30 dark:border-white/10 space-y-4">
      <div class="flex items-center justify-center gap-2">
        <a
          v-for="social in socialLinks"
          :key="social.name"
          :href="social.url"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="social.name"
          class="p-2.5 rounded-xl glass-subtle text-muted hover:text-primary transition-all"
        >
          <AppIcon :name="social.icon" :size="18" />
        </a>
      </div>
      <button
        v-if="isAdmin && isAuthenticated"
        type="button"
        class="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-muted hover:text-foreground rounded-xl glass-subtle glass-nav-hover transition-colors"
        @click="$emit('sign-out')"
      >
        <AppIcon name="LogOut" :size="16" />
        <span>Sign Out</span>
      </button>
      <router-link
        v-else
        to="/signin"
        class="flex items-center gap-2 px-3 py-2.5 text-sm text-muted hover:text-foreground rounded-xl glass-subtle glass-nav-hover transition-colors"
        @click="$emit('close-mobile')"
      >
        <AppIcon name="Settings" :size="16" />
        <span>Admin Sign In</span>
      </router-link>
    </div>
  </aside>
</template>

<script>
import AppIcon from '@/components/ui/AppIcon.vue'
import ProfileAvatar from '@/components/ui/ProfileAvatar.vue'
import { navItems, adminNavItems } from '@/data/profile'
import { images } from '@/data/images'
import { siteState } from '@/composables/useSiteData'

export default {
  name: 'AppSidebar',
  components: { AppIcon, ProfileAvatar },
  props: {
    mobileOpen: Boolean,
    isAdmin: Boolean,
    isAuthenticated: Boolean,
    activeSection: {
      type: String,
      default: 'home',
    },
  },
  emits: ['close-mobile', 'sign-out'],
  data() {
    return {
      siteState,
      images,
      items: navItems,
      adminItems: adminNavItems,
    }
  },
  computed: {
    socialLinks() {
      return [
        { name: 'Facebook', icon: 'Facebook', url: siteState.profile.social.facebook },
        { name: 'LinkedIn', icon: 'Linkedin', url: siteState.profile.social.linkedin },
        { name: 'Instagram', icon: 'Instagram', url: siteState.profile.social.instagram },
      ]
    },
  },
  methods: {
    navLinkClasses(section) {
      const isActive = this.$route.path === '/' && this.activeSection === section
      return [
        'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
        isActive ? 'glass-nav-active' : 'text-muted glass-nav-hover',
      ]
    },
    adminLinkClasses(path) {
      const isActive = path === '/admin' ? this.$route.path === '/admin' : this.$route.path.startsWith(path)
      return [
        'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
        isActive ? 'glass-nav-active' : 'text-muted glass-nav-hover',
      ]
    },
  },
}
</script>
